import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const timeline = [
  {
    year: '2020',
    title: 'Founded During the Pandemic',
    description: 'Born out of the urgent need for telehealth services across urban and rural settings when the world needed remote care most.',
  },
  {
    year: '2021',
    title: 'Pivoted to Mental Health',
    description: 'After receiving feedback from patients and providers, we focused on behavioral health, incorporating trained counselors and listeners.',
  },
  {
    year: '2022',
    title: 'CMS BHI Alignment',
    description: 'Aligned operations with CMS Behavioral Health Integration standards and developed bilingual patient engagement tools.',
  },
  {
    year: '2023',
    title: 'Clinical Team Expansion',
    description: 'Built teams of Behavioral Health Nurses embedded within practice care teams. Successfully deployed AI-powered digital solutions.',
  },
  {
    year: '2024',
    title: 'AI Platform Launch',
    description: 'Launched the full AI-powered platform with real-time session guidance, emotion detection, and automated documentation.',
  },
  {
    year: '2025+',
    title: 'Scaling Nationally',
    description: 'Expanding across the country with 300+ patients treated and continuous AI innovation.',
  },
];

export default function About() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section id="about" className="section-padding bg-white" ref={ref}>
      <div className="container-max">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Story */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 tracking-tight">
              Built by People Who{' '}
              <span className="gradient-text">Understand Care</span>
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Doctorite was founded during the COVID-19 pandemic when the need for
                accessible mental healthcare became undeniable. What started as a telehealth
                bridge grew into a mission to transform how behavioral health is delivered.
              </p>
              <p>
                Our team of healthcare professionals, AI engineers, and clinical experts
                saw a gap that no existing platform filled: behavioral health providers
                needed more than digitized paperwork. They needed an AI partner that could
                think with them during sessions, handle documentation automatically, and
                provide insights that improve outcomes.
              </p>
              <p>
                Based in Dallas, Texas, we're building the platform we wished existed
                when we first entered behavioral healthcare. Every feature is designed
                with providers, tested with providers, and refined by the practitioners
                who use it daily.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="p-4 bg-brand-50 rounded-xl">
                <div className="text-2xl font-extrabold text-brand-600">Dallas, TX</div>
                <div className="text-sm text-gray-600">Headquarters</div>
              </div>
              <div className="p-4 bg-accent-50 rounded-xl">
                <div className="text-2xl font-extrabold text-accent-600">2020</div>
                <div className="text-sm text-gray-600">Year Founded</div>
              </div>
            </div>
          </motion.div>

          {/* Right - Timeline */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-xl font-bold text-gray-900 mb-8">Our Journey</h3>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-brand-400 via-brand-300 to-accent-400" />

              <div className="space-y-6">
                {timeline.map((event, index) => (
                  <motion.div
                    key={event.year}
                    initial={{ opacity: 0, x: 20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    className="relative flex gap-4"
                  >
                    {/* Dot */}
                    <div className="relative z-10 shrink-0">
                      <div className="w-10 h-10 bg-white border-2 border-brand-300 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-brand-500 rounded-full" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="pb-2">
                      <div className="text-xs font-bold text-brand-500 uppercase tracking-wider mb-1">
                        {event.year}
                      </div>
                      <div className="text-base font-bold text-gray-900 mb-1">{event.title}</div>
                      <p className="text-sm text-gray-500 leading-relaxed">{event.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
